import { sanitizeReturnTo, useAuth } from '@frontend/auth';
import { AuthErrorState, AuthStatusMessage } from '@frontend/ui';
import { useEffect, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router';

/**
 * Deliberate login entry point. Kicks off Auth0's universal-login redirect,
 * carrying `returnTo` through `appState` so the post-callback navigation
 * restores the destination. A visit here is always an explicit choice, so —
 * unlike the guard gate — it starts a login even from `signedout`.
 */
export function LoginPage() {
  const { status, login } = useAuth();
  const [searchParams] = useSearchParams();
  // `returnTo` is an attacker-controllable query param; keep only a safe
  // same-origin path so neither <Navigate> nor the appState round-trip can be
  // steered off-origin.
  const returnTo = sanitizeReturnTo(searchParams.get('returnTo'));
  // Fire the redirect at most once per unauthed/signedout episode. Without
  // this, a StrictMode remount (dev) double-invokes the effect and starts
  // login twice; re-armed when status leaves both states, mirroring
  // useAuthGate's guard.
  const redirectStarted = useRef(false);

  useEffect(() => {
    if (status !== 'unauthed' && status !== 'signedout') {
      redirectStarted.current = false;
      return;
    }
    if (redirectStarted.current) return;
    redirectStarted.current = true;
    void login({ returnTo });
  }, [status, login, returnTo]);

  if (status === 'authed') {
    return <Navigate to={returnTo} replace />;
  }

  if (status === 'error') {
    // Bare login() — this page's returnTo comes from an untrusted query
    // param that could point at /login itself, so it must not round-trip.
    return (
      <AuthErrorState
        onRetry={() => {
          void login();
        }}
      />
    );
  }

  return <AuthStatusMessage>Redirecting to sign in…</AuthStatusMessage>;
}
